import { Role } from '@flowus/common';
import { LOCAL_LNG } from '@flowus/common/const';
import { cx } from '@flowus/common/cx';
import { BlockType, PermissionRole } from '@next-space/fe-api-idl';
import { debounce } from 'lodash-es';
import type { MouseEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { Icon } from 'src/common/components/icon';
import { ListView } from 'src/common/components/list-view';
import type { ListItem } from 'src/common/components/list-view/types';
import { ListItemType } from 'src/common/components/list-view/types';
import { useCloseModal, useOpenModal } from 'src/common/components/next-modal';
import { Tooltip } from 'src/common/components/tooltip';
import { useOpenMoveTo } from 'src/components/move-to';
import { useOpenCompleteDelete } from 'src/components/open-complete-delete';
import { UpdateUserInfo } from 'src/components/update-user-info';
import { createMenuDataContext } from 'src/editor/component/menu-list/menu-item';
import { ActivityIds } from 'src/hooks/activities/activity-ids';
import { ActivitiesListType } from 'src/hooks/activities/use-activity';
import { useUpdateTask } from 'src/hooks/activities/use-update-task';
import { useBlockLocked } from 'src/hooks/block/use-block-locked';
import { useBlockStatus } from 'src/hooks/block/use-block-status';
import { useMultipleDownload } from 'src/hooks/drive/use-multiple-download';
import { useReadonly } from 'src/hooks/page';
import { useSelectedBlockHistory } from 'src/hooks/page/use-selected-block-history';
import { useCanMove, useIsGuest, useParentIsOk } from 'src/hooks/share/use-permission-utils';
import { usePermissions } from 'src/hooks/share/use-permissions';
import { useGetShareHeaderMenuItems } from 'src/hooks/share/use-share-header-menu-list';
import { getCurrentSpace } from 'src/hooks/space';
import { useIsCustomTemplate } from 'src/hooks/space/use-is-custom-template';
import { useSpaceSecurityState } from 'src/hooks/space/use-space-security-state';
import { useTemplatesLimit } from 'src/hooks/space/use-templates-limit';
import { useTransaction } from 'src/hooks/use-transaction';
import { useUserName } from 'src/hooks/user/use-remark-name';
import { Modals } from 'src/modals';
import { HISTORY_REDO, HISTORY_UNDO } from 'src/redux/actions';
import { archiveBlock } from 'src/redux/managers/block/archive';
import { updateBlock } from 'src/redux/managers/block/update';
import * as TemplateManager from 'src/redux/managers/template';
import { dispatch, getState } from 'src/redux/store';
import { useSharedPages } from 'src/services/app';
import { $currentUserCache } from 'src/services/user/current-user';
import { bizTracker } from 'src/utils/biz-tracker';
import { isMindMap } from 'src/utils/block-type-utils';
import { writeTextInClipboard } from 'src/utils/clipboard';
import { judgeSharePage, useGetPageId } from 'src/utils/getPageId';
import { getLocationOrigin } from 'src/utils/location-utils';
import { usePickBlock } from 'src/utils/pick-block';
import { bindDataTestId, TestIds } from 'src/utils/qa-utils';
import { useIsInRight } from 'src/utils/right-utils';
import { ShortcutSystemSymbol } from 'src/utils/shortcut';
import { CommentTabIndex, setCommentListIndex } from 'src/views/comments/page-resolved-discussions';
import { leaveDoc } from '../aside/toc/more-bottom';
import { PageFeedTabIndex, setPageFeedIndex } from '../drawer/page-feeds/page-feeds';
import { useSetDrawerOpenState } from '../drawer/shared';
import { OpenSettingFrom, SettingMenuType } from '../setting-modal/type';
import { useOpenSettingModal, useOpenUpgradeSpace } from '../setting-modal/use-open-setting-modal';
import { CommentSettingList } from './comment-setting-list';
import { ExportFile } from './export-file';
import { ExportPanel } from './export-file/export-panel';
import { LayoutSwitcher } from './layout-switcher';
import { PageHistoryModal } from './page-history';

export const MenuButton = () => {
  const { t } = useAppTranslation();
  const uuid = useGetPageId();
  const openModal = useOpenModal();
  const isShared = judgeSharePage();
  const readonly = useReadonly(uuid);
  const pageBlock = usePickBlock(uuid, []);
  const parentIsOk = useParentIsOk(uuid);
  const isSharePage = judgeSharePage();

  const openMenuList = (event: MouseEvent<HTMLElement>) => {
    if (disabled) return;

    bizTracker.event('page_menu_click', {
      page_type: getState().blocks[uuid]?.type,
      is_share: isShared,
    });
    const popcorn = event.currentTarget;
    openModal.dropdown({
      modalId: Modals.PAGE_HEADER_MENU_LIST,
      popcorn,
      placement: 'bottom-end',
      offset: [0, 15],
      content() {
        return <MenuList popcorn={popcorn} uuid={uuid} />;
      },
    });
  };

  const IS_NOT_PAGE = pageBlock?.type !== BlockType.PAGE;
  // 分享页的菜单按钮不置灰; 多维表还需要根据父级判断（因为父级不行的话 菜单列表里都空了）
  const disabled = !isShared && (readonly || (IS_NOT_PAGE && !parentIsOk));

  if (isSharePage && __PRIVATE__) return null;

  return (
    <Tooltip
      popup={t('views.header.view_page_info')}
      placement="bottom"
      id="page-header-more"
      {...bindDataTestId(TestIds.pageHeaderMore)}
      disabled={disabled}
      onClick={openMenuList}
      className={cx(
        'p-1 flex items-center justify-center',
        disabled ? 'cursor-not-allowed' : 'animate-hover'
      )}
    >
      <Icon size="middle" name="IcMore" className={cx(disabled && 'opacity-20')} />
    </Tooltip>
  );
};

const MenuList = (props: { popcorn: HTMLElement; uuid: string }) => {
  const { t } = useAppTranslation();
  const { popcorn, uuid } = props;
  const isInRight = useIsInRight();
  const multipleDownload = useMultipleDownload();
  const isDeleted = useBlockStatus(uuid);
  const pageBlock = usePickBlock(
    uuid,
    ['data', 'updatedAt', 'updatedBy'],
    ['pageFixedWidth', 'directoryMenu', 'format', 'isByAI']
  );
  const userName = useUserName(pageBlock?.updatedBy ?? '');
  // 默认打开固定页面宽度
  const { pageFixedWidth = true, directoryMenu } = pageBlock?.data || {};
  const openMoveTo = useOpenMoveTo();
  const transaction = useTransaction();
  const { selectedBlockHistory } = useSelectedBlockHistory();
  const parentIsOk = useParentIsOk(uuid);
  const locked = useBlockLocked(uuid);
  const canMove = useCanMove(uuid);
  const menuItems = useGetShareHeaderMenuItems(uuid);
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const openCompleteDelete = useOpenCompleteDelete(uuid);
  const sharedPages = useSharedPages();
  const isGuest = useIsGuest();
  const isTemplate = useIsCustomTemplate(uuid);
  const limit = useTemplatesLimit();
  const openUpgradeSpace = useOpenUpgradeSpace();
  const updateTask = useUpdateTask();
  const setDrawerOpenState = useSetDrawerOpenState();
  const openSettingModal = useOpenSettingModal();
  const commentAlignment = pageBlock?.data.format?.commentAlignment ?? 'top';
  const abandonedExport = useSpaceSecurityState('abandonedExport');

  // 被风控了
  const { illegal, role } = usePermissions(uuid);
  const editorUser = [PermissionRole.WRITER, PermissionRole.EDITOR].includes(role);

  const isSharePage = judgeSharePage();
  const IS_NOT_PAGE = pageBlock?.type !== BlockType.PAGE;
  const isCollection =
    pageBlock?.type === BlockType.COLLECTION_VIEW ||
    pageBlock?.type === BlockType.COLLECTION_VIEW_PAGE ||
    pageBlock?.type === BlockType.REFERENCE_COLLECTION ||
    pageBlock?.type === BlockType.REFERENCE_COLLECTION_PAGE;
  const isFolder = pageBlock?.type === BlockType.FOLDER;
  const location = useLocation(); // 右侧panel的拷贝链接需要这个

  let items: ListItem[] = menuItems;
  if (!isSharePage) {
    if (illegal) {
      items = [
        // 如果是外部协作者，且 uuid 在 sharedPages 里认为是顶层
        isGuest && sharedPages.includes(uuid)
          ? {
              type: ListItemType.OPERATION,
              data: {
                icon: 'MIcSettingsLogout',
                title: t('views.header.leaveCollaboration'),
                onClick: () => {
                  void leaveDoc(uuid);
                },
              },
            }
          : {
              type: ListItemType.OPERATION,
              data: {
                icon: 'IcTrash',
                title: t('common.delete_completely'),
                onClick: () => openCompleteDelete(),
              },
            },
        {
          type: ListItemType.LINE,
          isHidden: !editorUser,
          data: {},
          disableClick: true,
        },
      ];
    } else {
      items = [
        {
          type: ListItemType.TEXT_SWITCH,
          isHidden: IS_NOT_PAGE,
          data: {
            title: LOCAL_LNG.fixedWidth,
            switch: pageFixedWidth,
            onSwitch: (status: boolean) => {
              bizTracker.event('fixedwidth', { is_open: status });
              transaction(() => {
                updateBlock(uuid, { data: { pageFixedWidth: status } });
              });
            },
          },
        },
        {
          type: ListItemType.TEXT_SWITCH,
          isHidden: IS_NOT_PAGE,
          data: {
            title: t('views.header.pageDirectory'),
            switch: directoryMenu,
            onSwitch: (status: boolean) => {
              bizTracker.event('float_catalog_switch', { is_open: status });
              transaction(() => {
                // 积分任务
                void updateTask(ActivityIds.CREATE_HEADER_BLOCK, ActivitiesListType.basicList, {
                  step2: true,
                });
                updateBlock(uuid, { data: { directoryMenu: status } });
              });
              // 多个用户反馈每个页面都需要手动打开，好烦。
              // 如果系统没有设置默认打开，并且用户在一个session中打开超过3次，我们有必要提示一下用户可以在设置中打开这个选项
              if (!status) return;
              const defaultOpen = $currentUserCache.setting?.directoryMenu;
              if (defaultOpen) return;
              const settingTipCount = parseInt(
                sessionStorage.getItem('settingTipCount') ?? '0',
                10
              );
              sessionStorage.setItem('settingTipCount', (settingTipCount + 1).toString());
              if (settingTipCount === 2) {
                openModal.warning({
                  title: t('views.header.autoOpenPageDirectory'),
                  content: t('views.header.settingPath'),
                  cancelText: t('common.cancel') as string,
                  colorType: 'active',
                  confirmText: t('common.go_to_settings') as string,
                  confirm: () => {
                    openSettingModal({
                      defaultMenuType: SettingMenuType.general,
                      from: OpenSettingFrom.tip,
                    });
                  },
                });
              }
            },
          },
        },
        {
          type: ListItemType.LINE,
          isHidden: IS_NOT_PAGE || !parentIsOk,
          data: {},
          disableClick: true,
        },
        {
          type: ListItemType.CUSTOM_RENDER,
          isHidden: isMindMap(pageBlock?.type),
          render: () => {
            return (
              <LayoutSwitcher
                className="px-4 my-3"
                pageId={uuid}
                hideLayoutComponent={isCollection || isFolder}
              />
            );
          },
        },
        {
          type: ListItemType.LINE,
          isHidden: isMindMap(pageBlock?.type),
          data: {},
          disableClick: true,
        },
        {
          type: ListItemType.OPERATION,
          isHidden: isMindMap(pageBlock?.type),
          data: {
            icon: 'IcComment',
            title: t('views.header.commentPosition'),
            hasArrow: true,
            renderSubMenu: () => {
              return (
                <CommentSettingList
                  commentAlignment={commentAlignment}
                  onChangeCommentAlignment={(v) => {
                    transaction(() => {
                      updateBlock(uuid, {
                        data: {
                          format: {
                            commentAlignment: v,
                          },
                        },
                      });
                    });
                  }}
                />
              );
            },
          },
        },
        {
          type: ListItemType.OPERATION,
          isHidden: IS_NOT_PAGE && !isCollection && !isMindMap(pageBlock?.type),
          data: {
            icon: locked ? 'IcUnlock' : 'IcLock',
            title: locked
              ? isCollection
                ? t('common.unlock_bitable')
                : t('views.header.unlockPage')
              : isCollection
              ? t('common.lock_bitable')
              : t('views.header.lockPage'),
            tip: isCollection ? t('views.header.collectionLockTip') : t('views.header.pageLockTip'),
            onClick: () => {
              bizTracker.event('page_lock', {
                is_open: !locked,
              });
              transaction(() => {
                updateBlock(uuid, {
                  data: {
                    format: {
                      locked: !locked,
                      lockedBy: $currentUserCache.uuid,
                    },
                  },
                });
              });
            },
            rightText: locked ? `${ShortcutSystemSymbol}+L` : `${ShortcutSystemSymbol}+L`,
          },
        },
        {
          type: ListItemType.LINE,
          isHidden: (IS_NOT_PAGE && !isCollection) || isFolder,
          data: {},
          disableClick: true,
        },
        {
          type: ListItemType.OPERATION,
          isHidden: !parentIsOk || role === PermissionRole.READER || role === PermissionRole.NONE,
          data: {
            icon: 'IcLink',
            title: t('common.copy_page_link'),
            onClick: debounce(
              () => {
                bizTracker.event('copy_page_link');
                void writeTextInClipboard(`${getLocationOrigin()}${location.pathname}`);
              },
              300,
              { leading: true }
            ),
          },
        },
        {
          type: ListItemType.LINE,
          isHidden: IS_NOT_PAGE || isDeleted || !parentIsOk,
          data: {},
          disableClick: true,
        },
        {
          type: ListItemType.OPERATION,
          isHidden: role === PermissionRole.READER || role === PermissionRole.NONE,
          data: {
            icon: 'IcUndo',
            rightText: `${ShortcutSystemSymbol}+Z`,
            title: t('common.undo'),
            onClick: debounce(
              () => {
                bizTracker.event('undo_click');
                dispatch(HISTORY_UNDO());
              },
              300,
              { leading: true }
            ),
          },
        },
        {
          type: ListItemType.OPERATION,
          isHidden: role === PermissionRole.READER || role === PermissionRole.NONE,
          data: {
            icon: 'IcRedo',
            rightText: `${ShortcutSystemSymbol}+Shift+Z`,
            title: t('common.redo'),
            onClick: debounce(
              () => {
                bizTracker.event('redo_click');
                dispatch(HISTORY_REDO());
              },
              300,
              { leading: true }
            ),
          },
        },
        {
          type: ListItemType.OPERATION,
          isHidden: role !== PermissionRole.EDITOR && role !== PermissionRole.WRITER,
          data: {
            icon: 'IcPageHistory',
            title: t('common.page_history'),
            onClick: debounce(
              () => {
                openModal.modal({
                  modalId: Modals.DOC_HISTORY_MODAL_ID,
                  content: () => {
                    return <PageHistoryModal docId={uuid} />;
                  },
                });
              },
              300,
              { leading: true }
            ),
          },
        },
        {
          type: ListItemType.OPERATION,
          // 可评论权限没有已解决评论入口，可编辑才有
          isHidden: !Role.contains(role, PermissionRole.WRITER),
          data: {
            icon: 'IcCommentResolved',
            title: t('common.resolved_comments'),
            onClick: debounce(
              () => {
                setCommentListIndex(CommentTabIndex.RESOLVE_COMMENT);
                setDrawerOpenState('page-resolved-discussions');
              },
              300,
              { leading: true }
            ),
          },
        },
        {
          type: ListItemType.OPERATION,
          isHidden:
            isDeleted ||
            !parentIsOk ||
            role === PermissionRole.READER ||
            role === PermissionRole.NONE,
          data: {
            icon: 'IcTrash',
            title: t('common.delete'),
            onClick: debounce(
              () => {
                bizTracker.event('delete_pageclick');
                transaction(() => {
                  archiveBlock(uuid);
                });
              },
              300,
              { leading: true }
            ),
          },
        },
        {
          type: ListItemType.LINE,
          data: {},
          disableClick: true,
        },
        {
          type: ListItemType.TEXT_SWITCH,
          isHidden:
            isFolder ||
            isDeleted ||
            isGuest ||
            role === PermissionRole.READER ||
            role === PermissionRole.NONE,
          data: {
            icon: 'IcTemplate',
            iconSize: 'small',
            title: t('views.header.convertToTemplate'),
            switch: isTemplate,
            context: createMenuDataContext({
              title: t('views.header.templateTip'),
              image: 'custom_template_tip',
            }),
            onSwitch: (status: boolean) => {
              transaction(() => {
                if (status) {
                  if (!limit) {
                    bizTracker.event('temp_switch', {
                      is_open: true,
                    });
                    TemplateManager.add(uuid, {
                      parentId: getCurrentSpace().uuid,
                      first: true,
                    });
                    updateBlock(uuid, {
                      data: {
                        createdTemplateBy: $currentUserCache.uuid,
                      },
                    });

                    // 新手任务
                    void updateTask(
                      ActivityIds.CONVERT_TO_TEMPLATE,
                      ActivitiesListType.advancedList
                    );

                    return;
                  }
                  openModal.warning({
                    title: (
                      <div className="mb-5">
                        <p className="text-t1-medium mb-2.5">
                          {t('views.header.templateLimitReached')}
                        </p>
                      </div>
                    ),
                    colorType: 'active',
                    confirmText: t('common.upgrade_space') as string,
                    confirm: () => {
                      openUpgradeSpace(OpenSettingFrom.temp_max);
                    },
                  });
                } else {
                  bizTracker.event('temp_switch', {
                    is_open: false,
                  });
                  TemplateManager.remove(uuid, {
                    parentId: getCurrentSpace().uuid,
                  });
                }
              });
            },
          },
        },
        {
          type: ListItemType.OPERATION,
          isHidden: isFolder,
          data: {
            disable: abandonedExport,
            icon: 'IcExportPage',
            title: t('common.export_page'),
            onClick: debounce(
              () => {
                openModal.modal({
                  content: ({ onCloseModal }) => {
                    if (isMindMap(pageBlock?.type)) {
                      return <ExportPanel onCloseModal={onCloseModal} id={uuid} />;
                    }
                    return <ExportFile onCloseModal={onCloseModal} />;
                  },
                });
              },
              300,
              { leading: true }
            ),
          },
        },
        {
          type: ListItemType.LINE,
          isHidden: !canMove || isFolder,
          data: {},
          disableClick: true,
        },
        {
          type: ListItemType.OPERATION,
          isHidden: !isFolder,
          data: {
            icon: 'IcDownload',
            title: t('common.download'),
            onClick: () => {
              void multipleDownload([uuid]);
            },
          },
        },
        {
          type: ListItemType.OPERATION,
          isHidden: !canMove,
          data: {
            icon: 'IcMove',
            rightText: `${ShortcutSystemSymbol}+Shift+P`,
            title: t('common.move_to'),
            onClick: () => {
              bizTracker.event('moveto_pageclick');
              selectedBlockHistory([]);
              openMoveTo({
                popcorn,
                uuid,
                placement: 'bottom-end',
                offset: [0, 15],
              });
            },
          },
        },
        {
          type: ListItemType.LINE,
          isHidden: !(editorUser && !isSharePage),
          data: {},
          disableClick: true,
        },
        {
          type: ListItemType.OPERATION,
          data: {
            icon: 'IcTimeline',
            title: t('common.page_activity'),
            onClick: () => {
              setPageFeedIndex(PageFeedTabIndex.PAGE_FEED);
              setDrawerOpenState(isInRight ? 'page-feeds-right' : 'page-feeds');
            },
          },
        },
        {
          type: ListItemType.OPERATION,
          data: {
            icon: 'IcInfo',
            title: t('views.header.view_page_info'),
            onClick: () => {
              setPageFeedIndex(PageFeedTabIndex.PAGE_INFO);
              setDrawerOpenState(isInRight ? 'page-feeds-right' : 'page-feeds');
            },
          },
        },
        {
          type: ListItemType.LINE,
          data: {},
          disableClick: true,
        },
      ];
    }
  }

  return (
    <ListView
      className="py-1 rounded-r w-64 next-modal max-h-[90vh] overflow-auto"
      items={items}
      customFooter={
        editorUser &&
        !isSharePage && (
          <UpdateUserInfo
            name={userName}
            updatedAt={pageBlock?.updatedAt}
            showWordCount={pageBlock?.type === BlockType.PAGE}
            showMindNodeCount={isMindMap(pageBlock?.type)}
            isByAI={pageBlock?.data.isByAI}
          />
        )
      }
      onItemClick={() => closeModal(Modals.PAGE_HEADER_MENU_LIST)}
    />
  );
};
